import { useQuery } from '@tanstack/react-query';
import { HttpService } from '../../../services/http/http';
import {
  PaymentOptionContentEntry,
  PaymentOptionContentType,
  PaymentOptionsContent,
} from '../../types/payment';
import { useMemo } from 'react';
import { useTenant } from 'lib/hooks/useTenant';
import { dealsawayFallbackData, gwatFallbackData } from 'data/payment-option';
import useTripQuery from './useTripQuery';
import {
  getCustomPaymentOptionContent,
  getDivisionPaymentOptionConfig,
} from 'data/custom-payment-options';
import { useNumberFormatter } from 'lib/hooks/useNumberFormatter/useNumberFormatter';

const httpService = new HttpService({});

const getByType = (
  paymentOptionContents: PaymentOptionsContent[],
  type: PaymentOptionContentType
) => {
  return paymentOptionContents.find((poc) => poc.type === type);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isPaymentOptionContentType = (
  value: unknown
): value is PaymentOptionContentType => {
  switch (value) {
    case 'Promotional':
    case 'Standard':
    case 'VIP':
    case 'LITE-VIP':
      return true;
    default:
      return false;
  }
};

const normalizeEntry = (
  value: unknown
): PaymentOptionContentEntry | undefined => {
  if (!isRecord(value) || typeof value.content !== 'string') return undefined;

  if (value.popup === null) {
    return { content: value.content, popup: null };
  }

  if (isRecord(value.popup) && typeof value.popup.content === 'string') {
    return {
      content: value.content,
      popup: { content: value.popup.content },
    };
  }

  if (value.popup === undefined) {
    return { content: value.content };
  }
};

export const normalizePaymentOptionContents = (
  value: unknown
): PaymentOptionsContent[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((content): PaymentOptionsContent[] => {
    if (
      !isRecord(content) ||
      typeof content.title !== 'string' ||
      !isPaymentOptionContentType(content.type) ||
      !Array.isArray(content.entries) ||
      content.entries.length === 0
    ) {
      return [];
    }

    const normalizedEntries = content.entries.map(normalizeEntry);
    if (normalizedEntries.some((entry) => entry === undefined)) return [];

    const entries = normalizedEntries.filter(
      (entry): entry is PaymentOptionContentEntry => entry !== undefined
    );

    return [
      {
        title: content.title,
        type: content.type,
        entries,
      },
    ];
  });
};

type ResolvePaymentOptionContentProps = {
  type: PaymentOptionContentType;
  remoteContents: PaymentOptionsContent[] | undefined;
  fallbackContents: PaymentOptionsContent[];
  customContent?: PaymentOptionsContent | null;
  isError: boolean;
};

export const resolvePaymentOptionContent = ({
  type,
  remoteContents,
  fallbackContents,
  customContent,
  isError,
}: ResolvePaymentOptionContentProps) => {
  if (customContent) return customContent;

  const remoteContent = remoteContents
    ? getByType(remoteContents, type)
    : undefined;
  if (remoteContent) return remoteContent;

  if (remoteContents || isError) {
    return getByType(fallbackContents, type);
  }
};

export const getPaymentOptionContentsQueryKey = (tenant: string) =>
  ['payment-option-contents', tenant] as const;

export const usePaymentOptionContents = () => {
  const tenant = process.env.REACT_APP_TENANT || 'gwat';
  const URL = `https://gwat-contents.globalworkandtravel.com/payment-options?platform=${tenant}`;
  const { isDA } = useTenant();
  const fallbackData = isDA ? dealsawayFallbackData : gwatFallbackData;
  const numberFormatter = useNumberFormatter();

  // Get trip data to check the trip code
  const { data: tripData, isLoading: isTripLoading } = useTripQuery();
  const tripCode = tripData?.result?.trip?.code;
  const tripDivision = tripData?.result?.division?.division;
  const formattedUnitAmount = numberFormatter.format(1);
  const divisionConfig = useMemo(
    () =>
      getDivisionPaymentOptionConfig(tenant, tripDivision, formattedUnitAmount),
    [tenant, tripDivision, formattedUnitAmount]
  );
  const isTripSelectionLoading =
    tenant === 'gwat' && (!tripData || isTripLoading);

  const { isLoading, data, isError } = useQuery(
    getPaymentOptionContentsQueryKey(tenant),
    async () => {
      const response = await httpService.get<unknown>(URL);
      return normalizePaymentOptionContents(response);
    },
    { staleTime: 1 * 60 * 1000 }
  );

  const promotionalPaymentOptionContent = useMemo(() => {
    if (isTripSelectionLoading) return undefined;

    // Check for custom content first
    const customContent =
      divisionConfig?.options.Promotional?.content ||
      (isDA ? null : getCustomPaymentOptionContent(tripCode, 'Promotional'));

    return resolvePaymentOptionContent({
      type: 'Promotional',
      remoteContents: data,
      fallbackContents: fallbackData,
      customContent,
      isError,
    });
  }, [
    data,
    isError,
    fallbackData,
    tripCode,
    isDA,
    divisionConfig,
    isTripSelectionLoading,
  ]);

  const standardPaymentOptionContent = useMemo(() => {
    if (isTripSelectionLoading) return undefined;

    // Check for custom content first
    const customContent =
      divisionConfig?.options.Standard?.content ||
      (isDA ? null : getCustomPaymentOptionContent(tripCode, 'Standard'));

    return resolvePaymentOptionContent({
      type: 'Standard',
      remoteContents: data,
      fallbackContents: fallbackData,
      customContent,
      isError,
    });
  }, [
    data,
    isError,
    fallbackData,
    tripCode,
    isDA,
    divisionConfig,
    isTripSelectionLoading,
  ]);

  const vipPaymentOptionContent = useMemo(() => {
    if (isLoading || isTripSelectionLoading) return null;

    // Check for custom content first
    const customContent = isDA
      ? null
      : getCustomPaymentOptionContent(tripCode, 'VIP');

    return resolvePaymentOptionContent({
      type: 'VIP',
      remoteContents: data,
      fallbackContents: fallbackData,
      customContent,
      isError,
    });
  }, [
    isLoading,
    data,
    isError,
    fallbackData,
    tripCode,
    isDA,
    isTripSelectionLoading,
  ]);

  const liteVipPaymentOptionContent = useMemo(() => {
    if (isLoading || isTripSelectionLoading) return null;

    // Check for custom content first
    const customContent = isDA
      ? null
      : getCustomPaymentOptionContent(tripCode, 'LITE-VIP');

    return resolvePaymentOptionContent({
      type: 'LITE-VIP',
      remoteContents: data,
      fallbackContents: fallbackData,
      customContent,
      isError,
    });
  }, [
    isLoading,
    data,
    isError,
    fallbackData,
    tripCode,
    isDA,
    isTripSelectionLoading,
  ]);

  return {
    isLoading: isLoading || isTripSelectionLoading,
    isError,
    divisionPaymentOptionConfig: isTripSelectionLoading ? null : divisionConfig,
    promotionalPaymentOptionContent,
    standardPaymentOptionContent,
    vipPaymentOptionContent,
    liteVipPaymentOptionContent,
  };
};
